import React, { FC } from "react";
import { Tag, Row } from "antd";
import styled from "styled-components";
import {
  ComponentCatalogModifierListInfo,
  Enum_Catalogmodifierlist_Selection_Type,
} from "../../queries/auto-generated/schemas";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../ducks";
import Modifier from "./modifier";
import catalog from "../../ducks/catalog";
import _ from "lodash";

const useFindModifierList = (info: ComponentCatalogModifierListInfo) => {
  const allModifierLists = useSelector(
    (state: RootState) => state.catalog.data.modifierList
  );

  const jooListId = info?.modifier_list?.id;

  if (!!jooListId && jooListId in allModifierLists) {
    return allModifierLists[jooListId];
  }

  const squareListId = info.modifier_list?.square_id;

  if (!!squareListId) {
    return _.find(allModifierLists, (list) => list.square_id === squareListId);
  }

  return undefined;
};

const ModifierGroup: FC<{
  info: ComponentCatalogModifierListInfo;
  catalogItemId: string;
}> = ({ info, catalogItemId }) => {
  const dispatch = useDispatch();

  const modifier_list = useFindModifierList(info);

  const selections = useSelector(
    (state: RootState) => state.catalog.currentItemSelectionChoices
  );
  const allModifiers = useSelector(
    (state: RootState) => state.catalog.data.modifiers
  );
  const modifiers =
    modifier_list?.modifiers?.map((m) => {
      return allModifiers[m?.id ?? ""];
    }) ?? [];

  const min = info.min_selected_modifiers ?? -1;
  const max = info.max_selected_modifiers ?? -1;

  // The selection type may be overridden by an item's modifier_list_info' min or max
  const selectionType =
    min > 1 || max > 1
      ? Enum_Catalogmodifierlist_Selection_Type.Multiple
      : modifier_list?.selection_type ??
        Enum_Catalogmodifierlist_Selection_Type.Single;

  const required = min > 0;
  const instructions = makeInstructions({
    min,
    max,
    selection_type: selectionType,
  });
  const selected_modifier_ids =
    selections?.modifications[info?.id ?? ""]?.selected ?? [];

  const ordinalSorted = _.sortBy(modifiers, [(o) => o?.ordinal]);

  const errorMessage = `Please do the following: ${instructions}`;
  return (
    <Container>
      <Title>{modifier_list?.name}</Title>
      {required && <Tag color="volcano">Required</Tag>}
      <div>{instructions}</div>
      <ErrorMessage visible={selections?.error ?? false}>
        {errorMessage}
      </ErrorMessage>

      <Row gutter={[12, 12]}>
        {ordinalSorted?.map((modifier) => {
          const selected = selected_modifier_ids.includes(
            modifier?.square_id ?? ""
          );
          return (
            <Modifier
              modifier={modifier}
              selected={selected}
              toggleSelected={() =>
                dispatch(
                  catalog.actions.updatedSelection({
                    itemId: catalogItemId,
                    modifierListInfoId: info.id,
                    modifierId: modifier?.square_id ?? "",
                    selectionType,
                  })
                )
              }
            />
          );
        })}
      </Row>
    </Container>
  );
};

const Container = styled.div``;
const Title = styled.h3`
  display: inline-block;
  padding-right: 1em;
`;
const ErrorMessage = styled.div<{ visible: boolean }>`
  color: red;
  visibility: ${(p) => (p.visible ? "initial" : "hidden")};
`;

const makeInstructions = ({
  min,
  max,
  selection_type,
}: {
  min: number;
  max: number;
  selection_type: Enum_Catalogmodifierlist_Selection_Type;
}): string =>
  max === min &&
  max === -1 &&
  selection_type === Enum_Catalogmodifierlist_Selection_Type.Single
    ? "Select 1 option"
    : max !== min
    ? max > 0
      ? `Select ${min > 0 ? min : "up"} to ${max} options`
      : min > 0
      ? `Choose at least ${min} options`
      : ""
    : max > 0
    ? `Select ${min} option${min > 1 ? "s" : ""}`
    : "";

export default ModifierGroup;
